import { isEan13, isSku } from '@stalvi/contracts';
import type { MeResponse, SuggestItem } from '@stalvi/contracts';
import { Camera, Loader2, Search as SearchIcon } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { BarcodeScanner } from '../components/BarcodeScanner';
import { Header } from '../components/Header';
import { useToast } from '../components/Toast';
import { useAbortRef } from '../hooks/useAbortRef';
import { isAbortError } from '../lib/abort';
import { ApiError, api } from '../lib/api';
import { useSearchBackend } from '../lib/searchBackend';

const DEBOUNCE_MS = 250;
const SCAN_UNKNOWN_RETRY_MS = 1000;

async function reportScanUnknown(ean13: string): Promise<void> {
  try {
    await api.scanUnknown(ean13);
  } catch (e) {
    if (!(e instanceof ApiError) || e.status < 500 || e.status >= 600) return;
    await new Promise((r) => setTimeout(r, SCAN_UNKNOWN_RETRY_MS));
    try {
      await api.scanUnknown(ean13);
    } catch {
      // fire-and-forget after 1 retry
    }
  }
}

export function Search({ me, onSignOut }: { me: MeResponse; onSignOut: () => void }) {
  const [q, setQ] = useState('');
  const [items, setItems] = useState<SuggestItem[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [emptySuggest, setEmptySuggest] = useState(false);
  const [scanAvailable, setScanAvailable] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const { next: nextAbort } = useAbortRef();
  // Flipping the bake-off backend (#199) re-runs the suggest effect on the same q.
  const backend = useSearchBackend();
  const scanTriggerRef = useRef<HTMLButtonElement>(null);
  const nav = useNavigate();
  const { show: showToast } = useToast();

  const goToList = useCallback((qStr: string) => nav(`/search?q=${encodeURIComponent(qStr.trim())}`), [nav]);

  useEffect(() => {
    let cancelled = false;
    const md = navigator.mediaDevices;
    if (!md || typeof md.getUserMedia !== 'function' || typeof md.enumerateDevices !== 'function') {
      return;
    }
    md.enumerateDevices()
      .then((devices) => {
        if (cancelled) return;
        if (devices.some((d) => d.kind === 'videoinput')) setScanAvailable(true);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  // `backend` is an intentional re-trigger dep (#199): flipping the bake-off
  // backend must re-run the debounced suggest on the current q. api.suggest reads
  // the live backend at call time, so it isn't referenced in the effect body.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional re-trigger dep (see note above)
  useEffect(() => {
    if (q.trim().length < 4) {
      setItems([]);
      setHasMore(false);
      setEmptySuggest(false);
      setLoading(false);
      return;
    }
    const t = setTimeout(() => {
      const ac = nextAbort();
      setLoading(true);
      api
        .suggest(q.trim(), ac.signal)
        .then((r) => {
          if (r.redirect) {
            nav(`/product/${r.redirect.productId}`);
            return;
          }
          setItems(r.items);
          setHasMore(r.hasMore);
          setEmptySuggest(r.items.length === 0);
          setLoading(false);
        })
        .catch((e) => {
          // Skip setLoading(false) on abort: the next effect iteration owns
          // loading (either schedules a new request that sets it, or hits the
          // early-return above which clears it). Clearing here would race the
          // synchronous setLoading(true) of the request that triggered the abort.
          if (isAbortError(e)) return;
          setItems([]);
          setEmptySuggest(true);
          setLoading(false);
        });
    }, DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [q, nav, nextAbort, backend]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const raw = q.trim();
    if (raw.length === 0) return;
    // EAN13 / SKU short-circuit at submit time (paste + enter case).
    if (isEan13(raw) || isSku(raw)) {
      const ac = nextAbort();
      try {
        const r = await api.suggest(raw, ac.signal);
        if (r.redirect) return nav(`/product/${r.redirect.productId}`);
      } catch (e) {
        if (isAbortError(e)) return;
        if (!(e instanceof ApiError)) throw e;
      }
    }
    goToList(raw);
  }

  async function onScanDecoded(ean13: string): Promise<void> {
    const ac = nextAbort();
    try {
      const r = await api.suggest(ean13, ac.signal);
      if (r.redirect) {
        setScannerOpen(false);
        nav(`/product/${r.redirect.productId}`);
        return;
      }
      if (r.items.length === 0) {
        setScannerOpen(false);
        goToList(ean13);
        void reportScanUnknown(ean13);
        return;
      }
      setScannerOpen(false);
    } catch (e) {
      if (isAbortError(e)) return;
      if (e instanceof ApiError && e.status >= 500) {
        showToast('Hubo un problema, intenta de nuevo', 'error');
        return;
      }
      setScannerOpen(false);
      showToast('No pudimos buscar el producto, intenta de nuevo', 'error');
    }
  }

  return (
    <div className="stalvi-app flex flex-col">
      <Header me={me} onSignOut={onSignOut} />
      <main className="flex-1 flex flex-col items-center justify-center px-6">
        <form className="w-full max-w-[640px]" onSubmit={submit} role="search">
          <label htmlFor="search" className="block mb-2 text-text-secondary text-sm">
            Busca por EAN13, SKU o descripción
          </label>
          <div className="input-wrap">
            <SearchIcon className="icon-l" size={18} />
            <input
              id="search"
              className="input"
              placeholder="Busca por EAN13, SKU o descripción"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              autoFocus
              autoComplete="off"
              spellCheck={false}
            />
            {loading && <Loader2 className="icon-r animate-spin" size={18} />}
            {scanAvailable && !loading && (
              <button
                type="button"
                ref={scanTriggerRef}
                aria-label="Escanear código de barras"
                className="icon-r p-1"
                onClick={() => setScannerOpen(true)}
              >
                <Camera size={18} />
              </button>
            )}
          </div>
          <p className="text-xs text-text-tertiary mt-2">Escribe al menos 4 caracteres</p>

          {q.trim().length >= 4 && (items.length > 0 || emptySuggest) && (
            <ul role="listbox" className="mt-3 card p-1 divide-y divide-border-subtle overflow-hidden">
              {items.map((it) => (
                <li key={it.productId} role="option" aria-selected="false">
                  <Link
                    to={`/product/${it.productId}`}
                    className="flex items-center justify-between gap-4 px-4 py-3 hover:bg-bg-subtle"
                  >
                    <span className="flex flex-col min-w-0">
                      <span className="font-medium truncate">{it.name}</span>
                      <span className="text-sm text-text-secondary truncate">{it.brand ?? '—'}</span>
                    </span>
                    {it.ean13 && <span className="mono text-xs text-text-tertiary">{it.ean13}</span>}
                  </Link>
                </li>
              ))}
              {hasMore && (
                <li
                  role="option"
                  aria-selected="false"
                  tabIndex={0}
                  aria-label="Hay más resultados, presiona Enter para verlos"
                  className="px-4 py-3 text-center text-text-tertiary cursor-pointer hover:bg-bg-subtle"
                  onClick={() => goToList(q)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') goToList(q);
                  }}
                >
                  …
                </li>
              )}
              {!hasMore && emptySuggest && items.length === 0 && (
                <li
                  role="option"
                  aria-selected="false"
                  tabIndex={0}
                  className="px-4 py-3 text-center text-text-tertiary cursor-pointer hover:bg-bg-subtle"
                  onClick={() => goToList(q)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') goToList(q);
                  }}
                >
                  Sin sugerencias — presiona Enter para ver el listado completo
                </li>
              )}
            </ul>
          )}
        </form>
      </main>
      {scannerOpen && (
        <BarcodeScanner
          onClose={() => setScannerOpen(false)}
          onDecoded={onScanDecoded}
          triggerRef={scanTriggerRef}
        />
      )}
    </div>
  );
}
